import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  Link,
  NumberInput,
  Tag,
} from '@carbon/react';
import { Play } from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { formatNumber } from 'utils/number';
import { parseUTCdateTime } from 'utils/dates';
import { acres, count, interestPercent } from '../shared/formatters';
import { runAuditAtom, runAuditLoadableAtom } from './atoms';
import type {
  AcreageDriftRow,
  AcreageDriftStats,
  AcreageThresholds,
  AcreageTier,
  AuditStatus,
} from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: true,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

/**
 * How each tier reads. `gross_below_net` is arithmetically impossible;
 * `gross_contradicted` rests on the polygon disagreeing with the record, which
 * is strong but not arithmetic, so it is coloured apart rather than as an error.
 */
const TIER_LABELS: Record<
  AcreageTier,
  { tag: 'red' | 'magenta'; label: string }
> = {
  gross_below_net: { tag: 'red', label: 'gross below net' },
  gross_contradicted: { tag: 'magenta', label: 'contradicted by polygon' },
};

/**
 * Mirrors DEFAULT_SHORTFALL_PERCENT and DEFAULT_CONTRADICTED_MULTIPLE on
 * `Query::SubtractAcreageDrift`. The server clamps and defaults whatever it is
 * sent, so these seed the inputs and stand in when a field is left empty.
 */
const DEFAULT_THRESHOLDS: AcreageThresholds = {
  shortfallPercent: 0.1,
  contradictedMultiple: 2,
};

/**
 * How long after the last keystroke a changed bar re-runs itself.
 *
 * Long enough to type "2.5" as one edit rather than three, short enough that it
 * reads as the page keeping up rather than as a delay. The scan is sub-second,
 * so this is most of the wait.
 */
const AUTO_RUN_DELAY_MS = 700;

/**
 * Whether a field currently holds a number.
 *
 * The fields keep their text between edits so a decimal point survives being
 * typed, which means they pass through states — "", "2." — that are not numbers
 * yet. Auto-running through those would scan at the fallback and rewrite what
 * someone is halfway through typing.
 *
 * @param {string} raw - The field's current text.
 * @return {boolean} Whether it parses.
 */
export const holdsANumber = (raw: string) =>
  raw.trim() !== '' && !Number.isNaN(Number(raw));

/**
 * Date only — the time of day is noise here, and these are compared across
 * records to spot a common load rather than read individually. UTC for that
 * same reason: rendered in the reader's zone, a bulk load that landed near
 * midnight splits across two dates depending on who is looking.
 *
 * @param {string | null | undefined} value - An ISO timestamp.
 * @return {string} The formatted date, or a placeholder when absent.
 */
const date = (value: string | null | undefined) => {
  if (!value) return '--';
  return parseUTCdateTime(value);
};

const columnHelper = createColumnHelper<AcreageDriftRow>();

/**
 * Opens in a new tab. Every link here points into the CRM so a reader can fix a
 * record without losing the worklist they are working down.
 *
 * @param {string} href - Where to go.
 * @param {string} label - What to show.
 * @return {JSX.Element} The link.
 */
const crmLink = (href: string, label: string) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </Link>
);

/**
 * The bars are baked into the columns because they decide which figures are
 * worth calling out: with the polygon bar at 5x, colouring a 3x row red would
 * be flagging a value the report no longer treats as contradicting, and the
 * same goes for a Net / Gross ratio inside the shortfall the run allowed.
 *
 * @param {AcreageThresholds} thresholds - The bars this report was built at.
 * @return {Array} The column definitions.
 */
const buildColumns = ({
  shortfallPercent,
  contradictedMultiple,
}: AcreageThresholds) => [
  columnHelper.accessor('tier', {
    id: 'tier',
    header: 'Issue',
    size: 180,
    cell: (info) => {
      const tier = info.getValue();
      return (
        <Tag type={TIER_LABELS[tier].tag} size="sm">
          {TIER_LABELS[tier].label}
        </Tag>
      );
    },
  }),
  columnHelper.accessor('subtractName', {
    id: 'subtractName',
    header: 'Subtract',
    size: 160,
    cell: (info) =>
      crmLink(
        `/crm/tracts_and_subtracts/${info.row.original.tractId}/edit`,
        info.getValue() ?? '--'
      ),
  }),
  columnHelper.accessor('tractName', {
    id: 'tractName',
    header: 'CRM Tract',
    size: 140,
    cell: (info) =>
      crmLink(
        `/crm/tracts_and_subtracts/${info.row.original.tractId}/edit`,
        info.getValue() ?? '--'
      ),
  }),
  columnHelper.accessor('accountName', {
    id: 'accountName',
    header: 'CRM Account',
    size: 240,
  }),
  // Sits up front because it is a triage field, not a footnote: these arrive in
  // bulk loads, so sorting by creation date groups a whole bad load together.
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created',
    size: 120,
    cell: (info) => date(info.getValue()),
  }),
  // Net, its units, gross, then the polygon — the order the record is read in.
  // Net Acres is Subtract_Acres__c exactly as Salesforce stores it, which is what
  // the CRM screen shows; Units says whether that figure is mineral or royalty
  // acres, and so how to read it against gross.
  columnHelper.accessor('recordedNetAcres', {
    id: 'recordedNetAcres',
    header: 'Net Acres',
    size: 130,
    cell: (info) => acres(info.getValue()),
  }),
  columnHelper.accessor('netAcreageUnits', {
    id: 'netAcreageUnits',
    header: 'Units',
    size: 90,
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('grossAcres', {
    id: 'grossAcres',
    header: 'Gross Acres',
    size: 130,
    cell: (info) => acres(info.getValue()),
  }),
  columnHelper.accessor('polygonAcres', {
    id: 'polygonAcres',
    header: 'Polygon GIS Acres',
    size: 165,
    cell: (info) => acres(info.getValue()),
  }),
  // The corroborating signal: the parcel the polygon describes, against the
  // acreage recorded for it.
  columnHelper.accessor('polygonMultiple', {
    id: 'polygonMultiple',
    header: 'Polygon / Gross',
    size: 150,
    cell: (info) => {
      const multiple = info.getValue();
      if (multiple === null || multiple === undefined) return '--';
      const label = `${formatNumber(multiple, 0, 1, 'decimal', true)}x`;
      return multiple > contradictedMultiple ? (
        <Tag type="red" size="sm">
          {label}
        </Tag>
      ) : (
        label
      );
    },
  }),
  // What the tier was actually judged on. On an NMA tract this equals Net Acres;
  // on an NRA tract it is Net Acres divided by the factor, converting royalty
  // acres to mineral acres so the comparison against gross is like-for-like.
  // Kept out of the primary group above so it is not mistaken for the record.
  columnHelper.accessor('mineralNetAcres', {
    id: 'mineralNetAcres',
    header: 'Net (mineral basis)',
    size: 165,
    cell: (info) => acres(info.getValue()),
  }),
  // The gap, in both units it gets judged in. Worth two columns because the
  // acreage figures render to two places, so a record 0.002 acres apart shows
  // the same number twice and there is nothing on the row to say why it was
  // flagged.
  //
  // The percentage is what the bar is set in, so it can be read against the
  // threshold directly; it is tagged once it passes that bar.
  columnHelper.accessor('shortfallPercent', {
    id: 'shortfallPercent',
    header: 'Shortfall %',
    size: 130,
    cell: (info) => {
      const percent = info.getValue();
      if (percent === null || percent === undefined) return '--';
      const label = `${formatNumber(percent, 0, 4, 'decimal', true)}%`;
      return percent > shortfallPercent ? (
        <Tag type="red" size="sm">
          {label}
        </Tag>
      ) : (
        label
      );
    },
  }),
  // And the absolute gap, which is the one that says whether a percentage is
  // reporting a real discrepancy or the width of a rounded entry. Four decimals
  // rather than the two the acreage columns use, since the whole point is the
  // difference those two decimals hide.
  columnHelper.accessor('shortfallAcres', {
    id: 'shortfallAcres',
    header: 'Shortfall (ac)',
    size: 140,
    cell: (info) => {
      const gap = info.getValue();
      if (gap === null || gap === undefined) return '--';
      return formatNumber(gap, 0, 4, 'decimal', true);
    },
  }),
  // The same comparison as a multiple, which is what stays readable once gross
  // is badly understated: the percentage saturates near 100% and 208x and 854x
  // become indistinguishable there. Left untagged on purpose — the percentage
  // beside it already carries the tag, and flagging one fact twice on a row
  // reads as two findings.
  columnHelper.accessor('netMultiple', {
    id: 'netMultiple',
    header: 'Net / Gross',
    size: 130,
    cell: (info) => {
      const ratio = info.getValue();
      if (ratio === null || ratio === undefined) return '--';
      return formatNumber(ratio, 0, 4, 'decimal', true);
    },
  }),
  // The royalty rate itself rather than the conversion factor derived from it.
  // The factor is the rate normalised to a 1/8 base (rate x 8), so it is the
  // rate that means something to a reader — 0.125 is a 1/8 lease.
  columnHelper.accessor('royaltyRate', {
    id: 'royaltyRate',
    header: 'Royalty Rate',
    size: 130,
    cell: (info) => {
      const rate = info.getValue();
      if (!rate) return '--';
      return formatNumber(rate, 0, 6, 'decimal', true);
    },
  }),
  // What BOS took as gross. Where this differs from the Salesforce gross it is
  // usually the net copied across, which is the defect itself.
  columnHelper.accessor('bosGrossAcres', {
    id: 'bosGrossAcres',
    header: 'BOS Gross',
    size: 130,
    cell: (info) => acres(info.getValue()),
  }),
  columnHelper.accessor('estate', {
    id: 'estate',
    header: 'Estate',
    size: 110,
    cell: (info) => info.getValue() ?? '--',
  }),
  // The consequence of the bad divisor. Exactly 100% is the tell: the acreage
  // weighting cancelled out and the quotient is the burden rate alone. The
  // comparison behind the highlight still runs on the stored fraction — only the
  // rendering is a percentage.
  columnHelper.accessor('nri', {
    id: 'nri',
    header: 'BOS NRI (%)',
    size: 140,
    cell: (info) => {
      const value = info.getValue();
      const pinned = (value ?? 0) >= 1;
      return (
        <span className={pinned ? styles.overOne : undefined}>
          {interestPercent(value)}
        </span>
      );
    },
  }),
  // A Working estate says the owner holds an operating interest, so a blank or
  // zero WI is a gap in the record rather than a legitimate zero.
  columnHelper.accessor('workingInterest', {
    id: 'workingInterest',
    header: 'BOS WI (%)',
    size: 150,
    cell: (info) => {
      if (!info.row.original.missingWorkingInterest) {
        return interestPercent(info.getValue());
      }
      return (
        <Tag type="red" size="sm">
          {info.getValue() === null ? 'missing' : 'zero'}
        </Tag>
      );
    },
  }),
  // How much is riding on the record today, for triage order.
  columnHelper.accessor('calculationCount', {
    id: 'calculationCount',
    header: 'Calculations',
    size: 130,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('unitCount', {
    id: 'unitCount',
    header: 'Units Affected',
    size: 140,
    meta: { type: 'integer' },
  }),
  // The rest of the provenance. Three dates that answer different questions:
  // when the record arrived, whether anyone has touched it since, and when the
  // wrong figure was last carried into a Bundle of Sticks. A Modified equal to
  // Created means the value came in wrong and has never been revisited.
  columnHelper.accessor('modifiedAt', {
    id: 'modifiedAt',
    header: 'SF Modified',
    size: 130,
    cell: (info) => date(info.getValue()),
  }),
  columnHelper.accessor('bosUpdatedAt', {
    id: 'bosUpdatedAt',
    header: 'BOS Updated',
    size: 130,
    cell: (info) => date(info.getValue()),
  }),
  columnHelper.accessor('createdBy', {
    id: 'createdBy',
    header: 'Created By',
    size: 160,
    cell: (info) => info.getValue() ?? '--',
  }),
];

/**
 * The filterable facts about a row, in two dimensions.
 *
 * Tiers are alternatives — a row is in exactly one — so selecting several
 * widens the view. Attributes are independent properties, so selecting several
 * narrows it. Keeping them apart is what lets both behave the way a reader
 * expects from one row of chips.
 */
interface FilterChip {
  key: string;
  label: string;
  tagType: 'red' | 'magenta';
  matches: (row: AcreageDriftRow) => boolean;
}

/**
 * The two tiers. These OR together: no record is in both, so ANDing them would
 * always give an empty table.
 */
const TIER_FILTERS: FilterChip[] = [
  {
    key: 'gross_below_net',
    label: 'Gross Below Net',
    tagType: 'red' as const,
    matches: (row: AcreageDriftRow) => row.tier === 'gross_below_net',
  },
  {
    key: 'gross_contradicted',
    label: 'Contradicted',
    tagType: 'magenta' as const,
    matches: (row: AcreageDriftRow) => row.tier === 'gross_contradicted',
  },
];

/**
 * Chips describing independent properties of a record. These AND together, so
 * selecting several narrows the view.
 */
const ATTRIBUTE_FILTERS: FilterChip[] = [
  {
    key: 'nri_pinned',
    label: 'NRI Pinned At 100%',
    tagType: 'magenta' as const,
    matches: (row: AcreageDriftRow) => (row.nri ?? 0) >= 1,
  },
  {
    key: 'missing_wi',
    label: 'Missing Working Interest',
    tagType: 'red' as const,
    matches: (row: AcreageDriftRow) => row.missingWorkingInterest,
  },
];

const ALL_FILTERS = [...TIER_FILTERS, ...ATTRIBUTE_FILTERS];

/**
 * What an input holds, as a number. The fields keep their text between edits so
 * a decimal point survives being typed; an empty or unparseable one falls back
 * to the default, which is what the server would have done with it anyway.
 *
 * @param {string} raw - The field's current text.
 * @param {number} fallback - The value to use when it is not a number.
 * @return {number} The threshold to run at.
 */
export const parseThreshold = (raw: string, fallback: number) => {
  const value = Number(raw);
  return raw.trim() === '' || Number.isNaN(value) ? fallback : value;
};

/** What the button says while the scan is in flight. */
const BUTTON_LABELS: Partial<Record<AuditStatus, string>> = {
  running: 'Running…',
};

export const SubtractAcreageDriftPage: FC = () => {
  const runAudit = useSetAtom(runAuditAtom);
  const runState = useAtomValue(runAuditLoadableAtom);

  const [rows, setRows] = useState<AcreageDriftRow[]>([]);
  const [stats, setStats] = useState<AcreageDriftStats | null>(null);
  const [capped, setCapped] = useState(false);
  const [active, setActive] = useState<Set<string>>(new Set());

  // Held as text so a decimal point can be typed without the field rewriting it
  // mid-keystroke, and parsed only when the audit is started.
  const [shortfallInput, setShortfallInput] = useState(
    String(DEFAULT_THRESHOLDS.shortfallPercent)
  );
  const [multipleInput, setMultipleInput] = useState(
    String(DEFAULT_THRESHOLDS.contradictedMultiple)
  );
  // What the table on screen was built at, which is not what the inputs hold
  // once someone has edited them without re-running.
  const [applied, setApplied] = useState<AcreageThresholds>(DEFAULT_THRESHOLDS);

  const startAudit = useCallback(() => {
    runAudit({
      shortfallPercent: parseThreshold(
        shortfallInput,
        DEFAULT_THRESHOLDS.shortfallPercent
      ),
      contradictedMultiple: parseThreshold(
        multipleInput,
        DEFAULT_THRESHOLDS.contradictedMultiple
      ),
    });
  }, [runAudit, shortfallInput, multipleInput]);

  const toggleFilter = useCallback((key: string) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  // One request, so the loadable IS the lifecycle. No channel to subscribe to,
  // no polling fallback, and no queued state — there is no worker to wait for.
  const status: AuditStatus =
    runState.state === 'loading'
      ? 'running'
      : runState.state === 'hasError'
        ? 'error'
        : rows.length > 0 || stats !== null
          ? 'complete'
          : 'idle';

  const error =
    runState.state === 'hasError'
      ? runState.error instanceof Error
        ? runState.error.message
        : 'The audit failed'
      : null;

  useEffect(() => {
    if (runState.state !== 'hasData' || !runState.data) return;

    const { thresholds } = runState.data;
    setRows(runState.data.data);
    setStats(runState.data.stats);
    setCapped(runState.data.capped);
    setApplied(thresholds);

    // Snap the fields to what the server actually used. It clamps out-of-range
    // values, so typing 9999 into the polygon bar comes back applied as 1000 —
    // and left unsnapped the field would disagree with the table forever, which
    // with auto-run means re-running forever. Rewriting the field also makes
    // the clamp visible instead of silent.
    setShortfallInput(String(thresholds.shortfallPercent));
    setMultipleInput(String(thresholds.contradictedMultiple));
  }, [runState]);

  const requested: AcreageThresholds = {
    shortfallPercent: parseThreshold(
      shortfallInput,
      DEFAULT_THRESHOLDS.shortfallPercent
    ),
    contradictedMultiple: parseThreshold(
      multipleInput,
      DEFAULT_THRESHOLDS.contradictedMultiple
    ),
  };

  const isRunning = status === 'running';

  // The bars are only read on a run, so an edited field describes a report that
  // does not exist yet. Only meaningful once there IS a report to be stale
  // against — before the first run the fields are just what you are about to
  // ask for, not a discrepancy. `stats` rather than a row count, because an
  // audit that found nothing is still a report.
  const needsRerun =
    !isRunning &&
    stats !== null &&
    holdsANumber(shortfallInput) &&
    holdsANumber(multipleInput) &&
    (requested.shortfallPercent !== applied.shortfallPercent ||
      requested.contradictedMultiple !== applied.contradictedMultiple);

  // Re-runs itself once the reader stops typing, so a changed bar applies
  // without anyone reaching for a button.
  //
  // The signature guard is not belt-and-braces. Anything that leaves the fields
  // disagreeing with the table after a run — a clamp the snap above somehow
  // missed, a rounding difference — would otherwise re-fire on its own result,
  // forever, against production. It fires once per distinct pair of values and
  // then waits for the reader.
  //
  // A failed run stops it for the same reason: the mismatch survives the
  // failure, so retrying on a timer would hammer an endpoint that is already
  // unhappy. After an error the buttons are the way back.
  const lastAutoRun = useRef<string | null>(null);
  const { shortfallPercent, contradictedMultiple } = requested;

  useEffect(() => {
    if (!needsRerun || status === 'error') return undefined;

    const signature = `${shortfallPercent}:${contradictedMultiple}`;
    if (lastAutoRun.current === signature) return undefined;

    const timer = setTimeout(() => {
      lastAutoRun.current = signature;
      startAudit();
    }, AUTO_RUN_DELAY_MS);

    return () => clearTimeout(timer);
  }, [needsRerun, status, shortfallPercent, contradictedMultiple, startAudit]);
  // PageHeader collapses a labelled action button to a 2.7rem square below
  // screen-xlg, hiding the label via `span { display: none }` and centring the
  // icon. Carbon renders children bare, so an unwrapped label was never hidden —
  // it clipped inside the square. Wrapped, with an icon to show in its place,
  // and aria-label so the button keeps an accessible name once the span leaves
  // the a11y tree.
  const buttonLabel = (isRunning && BUTTON_LABELS[status]) || 'Run audit';

  /**
   * The Run audit button, which appears twice — once in the page header and
   * once beside the fields it acts on, so a reader who has just changed a bar
   * does not have to travel to the top of the page to apply it.
   *
   * @param {string} id - Distinguishes the two instances.
   * @return {JSX.Element} The button.
   */
  const runButton = (id: string) => (
    <Button
      id={id}
      className={classNames({ [styles.needsRerun]: needsRerun })}
      kind="primary"
      size="sm"
      renderIcon={Play}
      iconDescription={buttonLabel}
      aria-label={buttonLabel}
      disabled={isRunning}
      onClick={startAudit}
    >
      <span>{buttonLabel}</span>
    </Button>
  );

  // Chips narrow what has already been fetched rather than re-running: the whole
  // population is a single scan, so asking the server for a subset would just
  // fetch rows already in hand. The thresholds above cannot work that way —
  // loosening one asks for rows the scan never returned — which is why they
  // alone need a re-run.
  //
  // Tiers OR together and attributes AND, so picking both tiers plus NRI Pinned
  // reads as "either tier, and pinned at 100%" — which is what a reader means by
  // it, and what ANDing throughout would have made impossible.
  const visibleRows = useMemo(() => {
    const chosen = (group: FilterChip[]) =>
      group.filter((filter) => active.has(filter.key));
    const tiers = chosen(TIER_FILTERS);
    const attributes = chosen(ATTRIBUTE_FILTERS);

    return rows.filter(
      (row) =>
        (tiers.length === 0 || tiers.some((f) => f.matches(row))) &&
        attributes.every((f) => f.matches(row))
    );
  }, [rows, active]);

  const columns = useMemo(() => buildColumns(applied), [applied]);

  return (
    <>
      <PageHeader
        breadcrumbs={[['/admin/dashboard', 'Admin']]}
        title="Subtract Acreage Drift"
        actions={runButton('run-audit-header')}
      />
      <div className={styles.sectionHeader}>
        <ul className={styles.legend}>
          <li>
            <strong>Gross Below Net</strong> — gross acreage recorded below the
            net <em>mineral</em> acres owned in the parcel. NRA values are
            converted to mineral acres before comparing (see{' '}
            <strong>Net (mineral basis)</strong>).
          </li>
          <li>
            <strong>Contradicted</strong> — <strong>Gross Acres</strong> and{' '}
            <strong>Polygon GIS Acres</strong> contradict each other: the
            subtract&apos;s own polygon draws a parcel several times the acreage
            recorded for it (see <strong>Polygon / Gross</strong>). Only flagged
            where gross also equals net, which leaves the interest with no
            acreage weighting at all — either on its own is legitimate, together
            they are not.
          </li>
          <li>
            <strong>NRI Pinned At 100%</strong> — the interest saturated the
            <code> nri &lt;= 1 </code> guard instead of tripping it.
          </li>
          <li>
            <strong>Missing Working Interest</strong> — a Working estate with no
            working interest recorded.
          </li>
        </ul>
        <div className={styles.sectionDescription}>
          The two numbers below set where each of the first two starts, and
          re-run the report a moment after you change them:
        </div>
        <ul className={styles.legend}>
          <li>
            <strong>Gross below net at __%</strong> — how far under the net a
            gross has to fall to count. Below this it is two fields rounded to
            different precisions, not a defect.
          </li>
          <li>
            <strong>Contradicted at __x</strong> — how many times the recorded
            gross the polygon has to be before it counts as contradicting it.
          </li>
        </ul>
        <div className={styles.sectionDescription}>
          Widening either does not hide records, it re-judges them: a subtract
          that stops being below net is then weighed on whether its polygon
          contradicts the figure.
        </div>
        <div
          className={classNames(styles.thresholdPanel, {
            [styles.thresholdPanelStale]: needsRerun,
          })}
        >
          <div className={styles.controls}>
            <NumberInput
              id="acreage-shortfall-percent"
              className={styles.thresholdField}
              label="Gross below net at"
              helperText="% under the mineral-acre net"
              min={0}
              max={99}
              step={0.5}
              value={shortfallInput}
              disabled={isRunning}
              onChange={(_event, { value }) => setShortfallInput(String(value))}
            />
            <NumberInput
              id="acreage-contradicted-multiple"
              className={styles.thresholdField}
              label="Contradicted at"
              helperText="x the recorded gross, by polygon"
              min={1}
              max={1000}
              step={0.5}
              value={multipleInput}
              disabled={isRunning}
              onChange={(_event, { value }) => setMultipleInput(String(value))}
            />
            <div className={styles.inlineRun}>
              {runButton('run-audit-inline')}
            </div>
          </div>
          {/* Said in words as well as colour: the pulse is suppressed under
              prefers-reduced-motion, and an animation alone would tell a screen
              reader nothing. role=status announces it without stealing focus,
              which matters when it appears while someone is still typing. */}
          {needsRerun && (
            <div className={styles.staleNotice} role="status">
              {`Re-running at ${formatNumber(
                requested.shortfallPercent,
                0,
                2,
                'decimal',
                true
              )}% and ${formatNumber(
                requested.contradictedMultiple,
                0,
                2,
                'decimal',
                true
              )}x — the table below is still at ${formatNumber(
                applied.shortfallPercent,
                0,
                2,
                'decimal',
                true
              )}% and ${formatNumber(
                applied.contradictedMultiple,
                0,
                2,
                'decimal',
                true
              )}x.`}
            </div>
          )}
        </div>
      </div>

      {status === 'idle' && (
        <InlineNotification
          kind="info"
          title="Not run yet"
          subtitle="Set the two thresholds and run the audit to build the report."
          hideCloseButton
        />
      )}

      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          hideCloseButton
        />
      )}

      {capped && (
        <InlineNotification
          kind="warning"
          title="Results truncated"
          subtitle="More subtracts have inconsistent acreage than this report returns. Raise either threshold and run again to narrow it."
          hideCloseButton
        />
      )}

      {stats && (
        <div className={styles.filterBar}>
          <Tag type="cool-gray" size="md">
            {`Showing ${count(visibleRows.length)} of ${count(
              stats.totalSubtracts
            )} subtracts`}
          </Tag>
          <Tag type="gray" size="md">
            {`Accounts: ${count(stats.accounts)}`}
          </Tag>
          {/* What the rows on screen were judged by, which the inputs above no
              longer say once they have been edited without re-running. */}
          <Tag type="gray" size="md">
            {`Built at: ${formatNumber(
              applied.shortfallPercent,
              0,
              2,
              'decimal',
              true
            )}% below net, ${formatNumber(
              applied.contradictedMultiple,
              0,
              2,
              'decimal',
              true
            )}x polygon`}
          </Tag>
          {ALL_FILTERS.map((filter) => {
            // Counts come from the whole loaded set, not the current view, so a
            // chip always says how many rows selecting it would reach rather
            // than collapsing to zero once something else is selected.
            const total = rows.filter(filter.matches).length;
            const selected = active.has(filter.key);
            return (
              <Tag
                key={filter.key}
                type={selected ? filter.tagType : 'outline'}
                size="md"
                filter={selected}
                onClick={() => toggleFilter(filter.key)}
                onClose={() => toggleFilter(filter.key)}
                title="Clear filter"
              >
                {`${filter.label}: ${count(total)}`}
              </Tag>
            );
          })}
          {active.size > 0 && (
            <Button kind="ghost" size="sm" onClick={() => setActive(new Set())}>
              Clear filters
            </Button>
          )}
        </div>
      )}

      {isRunning && rows.length === 0 && (
        <DataTableSkeleton columnCount={14} rowCount={10} />
      )}

      {/* Renders on the loaded set, not the filtered one, so narrowing to a tier
          with no rows shows an empty table rather than the page reverting to its
          pre-run state. */}
      {rows.length > 0 && (
        <DataTable
          data={visibleRows}
          columns={columns as ColumnDef<AcreageDriftRow>[]}
          getRowId={(row) => row.subtractId}
          persistenceKey="admin-subtract-acreage-drift"
          scroll={{ mode: 'paginated' }}
          {...TABLE_CONFIG}
        />
      )}
    </>
  );
};
